import React, { useEffect, useMemo, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { makeStyles } from '@material-ui/core/styles';

import DeploiementResume from './DeploiementResume';
import { creerDeploiement } from './deploiementApi';

const etapes = [
  'Contexte',
  'Validation Plesk',
  'Validation base de donnees',
  'Runbook migration + Publish',
  'Validation post-deploiement',
  'Decision',
  'Resume',
];

const CLE_BROUILLON_DEPLOIEMENT = 'deploiement_plateformeweb_brouillon_v1';
const DOSSIER_BACKEND_SYNCSTATSLIVE = 'C:\\Donnees\\source\\repos\\plateformeweb\\SyncStatsLive\\SyncStatsLive';
const COMMANDE_MIGRATIONS_LIST = 'dotnet ef migrations list';
const COMMANDE_MIGRATION_DEV_ENV = 'set ASPNETCORE_ENVIRONMENT=Staging';
const COMMANDE_MIGRATION_PROD_ENV = 'set ASPNETCORE_ENVIRONMENT=Production';
const COMMANDE_DBCONTEXT_INFO = 'dotnet ef dbcontext info';
const COMMANDE_MIGRATION_UPDATE = 'dotnet ef database update';

const useStyles = makeStyles(() => ({
  bulleInfo: {
    fontSize: '0.95rem',
    lineHeight: 1.45,
    maxWidth: 460,
    padding: '10px 12px',
  },
}));

const etatInitial = {
  environnementCible: '',
  operateur: '',
  branche: '',
  commit: '',
  version: '',
  validationsPlesk: {
    siteActif: false,
    espaceDisqueSuffisant: false,
    connexionDbOk: false,
    sslValide: false,
    variablesEnvironnementOk: false,
    isolationRacinesOk: false,
  },
  validationsBaseDonnees: {
    migrationNecessaire: '',
    migrationSensible: false,
    migrationValidee: false,
    rollbackPrepare: false,
    notes: '',
  },
  validationsRunbook: {
    migrationDevEnvSet: false,
    migrationDevMigrationsList: false,
    migrationDevDbContextInfo: false,
    migrationDevDatabaseUpdate: false,
    migrationDevHistoriqueVerifie: false,
    publishDevPrereqCompileLocal: false,
    publishDevPrereqProfilsExistants: false,
    publishDevOuvrirSolution: false,
    publishDevClicDroitPublish: false,
    publishDevSelectionProfilDev: false,
    publishDevParametresVerifies: false,
    publishDevValidateConnection: false,
    publishDevSettingsVerifies: false,
    publishDevCliquerPublish: false,
    validationDevSiteCharge: false,
    validationDevLoginOk: false,
    validationDevCalendrierOk: false,
    validationDevAjoutPresenceOk: false,
    validationDevLogsSansErreurCritique: false,
    validationDevPasErreurSchemaSql: false,
    validationDevAckOutboxOk: false,
    validationDevStopSiKo: false,
    migrationProdEnvSet: false,
    migrationProdMigrationsList: false,
    migrationProdDbContextInfo: false,
    migrationProdDatabaseUpdate: false,
    migrationProdHistoriqueVerifie: false,
    publishProdVerificationRapideFaite: false,
    publishProdClicDroitPublish: false,
    publishProdSelectionProfilProd: false,
    publishProdParametresVerifies: false,
    publishProdValidateConnection: false,
    publishProdDatabasePublishDesactive: false,
    publishProdCliquerPublish: false,
    validationProdSiteAccessible: false,
    validationProdLoginOk: false,
    validationProdFluxPrincipalOk: false,
    validationProdLogsSansErreurCritique: false,
    validationProdPasMigrationAuto: false,
    validationProdAckOutboxOk: false,
  },
  validationsPostDeploiement: {
    navigationBaseOk: false,
    connexionUtilisateurOk: false,
    parcoursPresenceOk: false,
    ackSyncOk: false,
    logsSansErreurCritique: false,
    notes: '',
  },
  decisionFinale: '',
  commentaireDecision: '',
  incidents: '',
};

const verifierContexte = (etat) => {
  if (!etat.environnementCible || !etat.operateur.trim()) {
    return false;
  }
  return Boolean(etat.branche.trim() || etat.commit.trim() || etat.version.trim());
};

const verifierPlesk = (etat) => Object.values(etat.validationsPlesk).every(Boolean);

const verifierDb = (etat) => {
  if (!etat.validationsBaseDonnees.migrationNecessaire) {
    return false;
  }
  if (!etat.validationsBaseDonnees.migrationValidee) {
    return false;
  }
  if (etat.validationsBaseDonnees.migrationSensible && !etat.validationsBaseDonnees.rollbackPrepare) {
    return false;
  }
  return true;
};

const verifierPost = (etat) => {
  const checks = etat.validationsPostDeploiement;
  return Boolean(
    checks.navigationBaseOk &&
    checks.connexionUtilisateurOk &&
    checks.parcoursPresenceOk &&
    checks.ackSyncOk &&
    checks.logsSansErreurCritique
  );
};

const progressionRunbook = (r) => {
  const migrationDev = Boolean(
    r.migrationDevEnvSet &&
    r.migrationDevMigrationsList &&
    r.migrationDevDbContextInfo &&
    r.migrationDevDatabaseUpdate &&
    r.migrationDevHistoriqueVerifie
  );
  const publishDev = Boolean(
    r.publishDevPrereqCompileLocal &&
    r.publishDevPrereqProfilsExistants &&
    r.publishDevOuvrirSolution &&
    r.publishDevClicDroitPublish &&
    r.publishDevSelectionProfilDev &&
    r.publishDevParametresVerifies &&
    r.publishDevValidateConnection &&
    r.publishDevSettingsVerifies &&
    r.publishDevCliquerPublish
  );
  const validationDev = Boolean(
    r.validationDevSiteCharge &&
    r.validationDevLoginOk &&
    r.validationDevCalendrierOk &&
    r.validationDevAjoutPresenceOk &&
    r.validationDevLogsSansErreurCritique &&
    r.validationDevPasErreurSchemaSql &&
    r.validationDevAckOutboxOk &&
    r.validationDevStopSiKo
  );
  const migrationProd = Boolean(
    r.migrationProdEnvSet &&
    r.migrationProdMigrationsList &&
    r.migrationProdDbContextInfo &&
    r.migrationProdDatabaseUpdate &&
    r.migrationProdHistoriqueVerifie
  );
  const publishProd = Boolean(
    r.publishProdVerificationRapideFaite &&
    r.publishProdClicDroitPublish &&
    r.publishProdSelectionProfilProd &&
    r.publishProdParametresVerifies &&
    r.publishProdValidateConnection &&
    r.publishProdDatabasePublishDesactive &&
    r.publishProdCliquerPublish
  );
  const validationProd = Boolean(
    r.validationProdSiteAccessible &&
    r.validationProdLoginOk &&
    r.validationProdFluxPrincipalOk &&
    r.validationProdLogsSansErreurCritique &&
    r.validationProdPasMigrationAuto &&
    r.validationProdAckOutboxOk
  );

  return { migrationDev, publishDev, validationDev, migrationProd, publishProd, validationProd };
};

const verifierRunbook = (etat) => {
  const p = progressionRunbook(etat.validationsRunbook || {});
  return Boolean(p.migrationDev && p.publishDev && p.validationDev && p.migrationProd && p.publishProd && p.validationProd);
};

const verifierDecision = (etat) => Boolean(etat.decisionFinale);

const evaluerEtape = (etat, index) => {
  if (index === 0) return verifierContexte(etat);
  if (index === 1) return verifierPlesk(etat);
  if (index === 2) return verifierDb(etat);
  if (index === 3) return verifierRunbook(etat);
  if (index === 4) return verifierPost(etat);
  if (index === 5) return verifierDecision(etat);
  if (index === 6) return verifierContexte(etat) && verifierPlesk(etat) && verifierDb(etat) && verifierRunbook(etat) && verifierPost(etat) && verifierDecision(etat);
  return false;
};

const construireRapportStructure = (etat) => {
  const dateRapport = new Date().toISOString();
  return {
    type: 'deploiement_plateformeweb',
    dateRapport,
    environnementCible: etat.environnementCible,
    operateur: etat.operateur,
    referenceVersion: {
      branche: etat.branche,
      commit: etat.commit,
      version: etat.version,
    },
    validationsPlesk: { ...etat.validationsPlesk },
    validationsBaseDonnees: { ...etat.validationsBaseDonnees },
    validationsRunbook: { ...etat.validationsRunbook },
    validationsPostDeploiement: { ...etat.validationsPostDeploiement },
    decisionFinale: etat.decisionFinale,
    commentaireDecision: etat.commentaireDecision,
    incidents: etat.incidents,
    rapport: {
      titre: 'Rapport de deploiement plateformeweb',
      sections: [
        {
          nom: 'Contexte',
          contenu: {
            environnementCible: etat.environnementCible,
            operateur: etat.operateur,
            branche: etat.branche,
            commit: etat.commit,
            version: etat.version,
          },
        },
        {
          nom: 'Validation Plesk',
          contenu: { ...etat.validationsPlesk },
        },
        {
          nom: 'Validation DB / migration / rollback',
          contenu: { ...etat.validationsBaseDonnees },
        },
        {
          nom: 'Validation runbook migration + Publish',
          contenu: { ...etat.validationsRunbook },
        },
        {
          nom: 'Validation post-deploiement',
          contenu: { ...etat.validationsPostDeploiement },
        },
        {
          nom: 'Decision finale',
          contenu: {
            decisionFinale: etat.decisionFinale,
            commentaireDecision: etat.commentaireDecision,
            incidents: etat.incidents,
          },
        },
      ],
    },
  };
};

function DeploiementForm({ onCreation }) {
  const classes = useStyles();
  const [etat, setEtat] = useState(etatInitial);
  const [etapeActive, setEtapeActive] = useState(0);
  const [soumissionEnCours, setSoumissionEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [pretSauvegarde, setPretSauvegarde] = useState(false);
  const [derniereSauvegarde, setDerniereSauvegarde] = useState('');
  const [runbookSousEtape, setRunbookSousEtape] = useState('migrationDev');
  const [afficherErreursRunbook, setAfficherErreursRunbook] = useState(false);

  const rapport = useMemo(() => construireRapportStructure(etat), [etat]);
  const peutContinuer = evaluerEtape(etat, etapeActive);
  const peutSoumettre = evaluerEtape(etat, 6);

  const changerChamp = (nom, valeur) => setEtat((precedent) => ({ ...precedent, [nom]: valeur }));
  const changerPlesk = (nom, valeur) => setEtat((precedent) => ({ ...precedent, validationsPlesk: { ...precedent.validationsPlesk, [nom]: valeur } }));
  const changerDb = (nom, valeur) => setEtat((precedent) => ({ ...precedent, validationsBaseDonnees: { ...precedent.validationsBaseDonnees, [nom]: valeur } }));
  const changerRunbook = (nom, valeur) => setEtat((precedent) => ({ ...precedent, validationsRunbook: { ...precedent.validationsRunbook, [nom]: valeur } }));
  const changerPost = (nom, valeur) => setEtat((precedent) => ({ ...precedent, validationsPostDeploiement: { ...precedent.validationsPostDeploiement, [nom]: valeur } }));

  const copierTexte = async (texte, libelle) => {
    try {
      await navigator.clipboard.writeText(texte);
      setSucces('Commande copiee: ' + libelle);
    } catch (erreurCopie) {
      setErreur('Impossible de copier automatiquement. Merci de copier la commande manuellement.');
    }
  };

  useEffect(() => {
    try {
      const sauvegardeBrute = localStorage.getItem(CLE_BROUILLON_DEPLOIEMENT);
      if (sauvegardeBrute) {
        const sauvegarde = JSON.parse(sauvegardeBrute);
        if (sauvegarde && sauvegarde.etat) {
          setEtat({ ...etatInitial, ...sauvegarde.etat });
          setEtapeActive(typeof sauvegarde.etapeActive === 'number' ? sauvegarde.etapeActive : 0);
          if (sauvegarde.derniereSauvegarde) {
            setDerniereSauvegarde(sauvegarde.derniereSauvegarde);
          }
          setSucces('Un brouillon en cours a ete restaure automatiquement.');
        }
      }
    } catch (erreurChargement) {
      setErreur('Impossible de restaurer le brouillon local.');
    } finally {
      setPretSauvegarde(true);
    }
  }, []);

  useEffect(() => {
    if (!pretSauvegarde) {
      return;
    }
    try {
      const formulaireInitial = JSON.stringify(etat) === JSON.stringify(etatInitial) && etapeActive === 0;
      if (formulaireInitial) {
        localStorage.removeItem(CLE_BROUILLON_DEPLOIEMENT);
        setDerniereSauvegarde('');
        return;
      }
      const horodatage = new Date().toISOString();
      localStorage.setItem(CLE_BROUILLON_DEPLOIEMENT, JSON.stringify({
        etat,
        etapeActive,
        derniereSauvegarde: horodatage,
      }));
      setDerniereSauvegarde(horodatage);
    } catch (erreurSauvegarde) {
      setErreur('Impossible de sauvegarder le brouillon local.');
    }
  }, [etat, etapeActive, pretSauvegarde]);

  const allerSuivant = () => {
    if (!peutContinuer) {
      setErreur("Des champs critiques sont manquants pour cette etape.");
      if (etapeActive === 3) {
        setAfficherErreursRunbook(true);
      }
      return;
    }
    setEtapeActive((precedent) => Math.min(precedent + 1, etapes.length - 1));
  };

  const allerPrecedent = () => setEtapeActive((precedent) => Math.max(precedent - 1, 0));

  const soumettre = async () => {
    if (!peutSoumettre) {
      setErreur('Le formulaire est incomplet. Merci de terminer toutes les validations critiques.');
      return;
    }
    setSoumissionEnCours(true);
    setErreur('');
    try {
      const resultat = await creerDeploiement(rapport);
      setSucces('Rapport de deploiement enregistre avec succes.');
      setEtat(etatInitial);
      setEtapeActive(0);
      localStorage.removeItem(CLE_BROUILLON_DEPLOIEMENT);
      setDerniereSauvegarde('');
      if (onCreation) {
        onCreation(resultat);
      }
    } catch (err) {
      setErreur(err.message || "Impossible d'enregistrer le rapport.");
    } finally {
      setSoumissionEnCours(false);
    }
  };

  const rendreContexte = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="env-cible-label">Environnement cible</InputLabel>
          <Select
            labelId="env-cible-label"
            value={etat.environnementCible}
            onChange={(event) => changerChamp('environnementCible', event.target.value)}
            label="Environnement cible"
          >
            <MenuItem value="dev">dev</MenuItem>
            <MenuItem value="prod">prod</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          variant="outlined"
          label="Operateur"
          value={etat.operateur}
          onChange={(event) => changerChamp('operateur', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          variant="outlined"
          label="Version"
          value={etat.version}
          onChange={(event) => changerChamp('version', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          variant="outlined"
          label="Branche"
          value={etat.branche}
          onChange={(event) => changerChamp('branche', event.target.value)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          variant="outlined"
          label="Commit"
          value={etat.commit}
          onChange={(event) => changerChamp('commit', event.target.value)}
          helperText="Au moins un champ parmi branche/commit/version est obligatoire"
        />
      </Grid>
    </Grid>
  );

  const rendreCase = (label, valeur, onChange, enErreur) => (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
      <FormControlLabel
        style={{ marginLeft: 0, marginRight: 0 }}
        control={<Checkbox color="primary" checked={valeur} onChange={(event) => onChange(event.target.checked)} />}
        label={<Typography color={enErreur ? 'error' : 'initial'}>{label}</Typography>}
      />
    </div>
  );

  const rendreCaseAvecInfo = (label, valeur, onChange, consigne, enErreur) => (
    <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <FormControlLabel
        style={{ marginLeft: 0, marginRight: 0 }}
        control={<Checkbox color="primary" checked={valeur} onChange={(event) => onChange(event.target.checked)} />}
        label={<Typography color={enErreur ? 'error' : 'initial'}>{label}</Typography>}
      />
      <Tooltip title={consigne} placement="right" arrow classes={{ tooltip: classes.bulleInfo }}>
        <IconButton size="medium" aria-label={'Informations - ' + label}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const rendreLibelleAvecInfo = (libelle, consigne) => (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
      <Typography variant="body2"><strong>{libelle}</strong></Typography>
      <Tooltip title={consigne} placement="right" arrow classes={{ tooltip: classes.bulleInfo }}>
        <IconButton size="small" aria-label={'Informations - ' + libelle}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const rendrePlesk = () => (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        {rendreCaseAvecInfo('Site actif dans Plesk', etat.validationsPlesk.siteActif, (value) => changerPlesk('siteActif', value), 'Ouvrez Websites & Domains et confirmez que le site cible est Active avant publication.')}
      </Grid>
      <Grid item xs={12}>
        {rendreCaseAvecInfo('Espace disque suffisant', etat.validationsPlesk.espaceDisqueSuffisant, (value) => changerPlesk('espaceDisqueSuffisant', value), 'Controlez le quota de stockage et validez un seuil de securite avant de publier.')}
      </Grid>
      <Grid item xs={12}>
        {rendreCaseAvecInfo('Connexion DB OK', etat.validationsPlesk.connexionDbOk, (value) => changerPlesk('connexionDbOk', value), 'Testez la connectivite SQL sur l environnement cible et confirmez la bonne chaine de connexion.')}
      </Grid>
      <Grid item xs={12}>
        {rendreCaseAvecInfo('Certificat SSL valide', etat.validationsPlesk.sslValide, (value) => changerPlesk('sslValide', value), 'Verifiez la validite du certificat SSL et son affectation au bon domaine.')}
      </Grid>
      <Grid item xs={12}>
        {rendreCaseAvecInfo('Variables d environnement ASP.NET Core conformes', etat.validationsPlesk.variablesEnvironnementOk, (value) => changerPlesk('variablesEnvironnementOk', value), 'Confirmez ASPNETCORE_ENVIRONMENT: Staging pour dev et Production pour prod.')}
      </Grid>
      <Grid item xs={12}>
        {rendreCaseAvecInfo('Isolation des racines web verifiee', etat.validationsPlesk.isolationRacinesOk, (value) => changerPlesk('isolationRacinesOk', value), 'Dans Plesk, comparez le chemin physique de dev et de prod: ils doivent etre differents. Test concret: creez un fichier marqueur dans prod, confirmez qu il n apparait pas en dev, puis supprimez-le.')}
      </Grid>
      <Grid item xs={12}>
        <Typography variant="caption" color="textSecondary">
          Survolez l icone info pour afficher la consigne detaillee de chaque verification.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          Exemple de validation d isolation: 1) relevez les chemins physiques dev/prod dans Plesk; 2) deposez `marqueur-prod.txt` en prod; 3) verifiez son absence en dev; 4) supprimez le marqueur.
        </Typography>
      </Grid>
    </Grid>
  );

  const rendreDb = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        {rendreLibelleAvecInfo(
          'Migration necessaire',
          'Choisissez Oui si la version deployee exige une evolution du schema SQL (nouvelle table, colonne, index, contrainte, etc.).'
        )}
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="migration-necessaire-label">Migration necessaire</InputLabel>
          <Select
            labelId="migration-necessaire-label"
            value={etat.validationsBaseDonnees.migrationNecessaire}
            onChange={(event) => changerDb('migrationNecessaire', event.target.value)}
            label="Migration necessaire"
          >
            <MenuItem value="oui">oui</MenuItem>
            <MenuItem value="non">non</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        {rendreCaseAvecInfo(
          'Migration sensible',
          etat.validationsBaseDonnees.migrationSensible,
          (value) => changerDb('migrationSensible', value),
          'Cochez si la migration est risquee: gros volume, verrouillage long, renommage, suppression, index lourds ou impact service.'
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        {rendreCaseAvecInfo(
          'Migration validee',
          etat.validationsBaseDonnees.migrationValidee,
          (value) => changerDb('migrationValidee', value),
          'Validez uniquement apres migration SQL manuelle (avant Publish) et verification de la derniere migration dans __EFMigrationsHistory.'
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        {rendreCaseAvecInfo(
          'Rollback prepare',
          etat.validationsBaseDonnees.rollbackPrepare,
          (value) => changerDb('rollbackPrepare', value),
          'Rollback attendu: sauvegarde/verif backup, etapes de retour arriere documentees, responsable identifie, duree estimee et criteres de declenchement.'
        )}
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          label="Notes DB / migration"
          value={etat.validationsBaseDonnees.notes}
          onChange={(event) => changerDb('notes', event.target.value)}
          helperText="Si migration sensible = Oui, le rollback prepare devient obligatoire."
        />
      </Grid>
      <Grid item xs={12}>
        <Paper style={{ padding: 12, background: '#fffaf2' }}>
          <Typography variant="subtitle2">Surveillance et rollback</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            A surveiller: erreurs SQL de schema, `Database.Migrate()` inattendu en hebergement, timeout/deadlocks, endpoints critiques et login.
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
            Rollback minimal: restaurer backup SQL, redeployer la version precedente, verifier les parcours critiques, tracer l incident.
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );

  const rendreRunbook = () => {
    const r = etat.validationsRunbook || {};
    const p = progressionRunbook(r);
    const enErreur = (cle) => Boolean(afficherErreursRunbook && !r[cle]);
    const sousEtapes = [
      { id: 'migrationDev', label: '1) Migration DEV', complete: p.migrationDev },
      { id: 'publishDev', label: '2) Publish DEV', complete: p.publishDev },
      { id: 'validationDev', label: '3) Validation DEV', complete: p.validationDev },
      { id: 'migrationProd', label: '4) Migration PROD', complete: p.migrationProd },
      { id: 'publishProd', label: '5) Publish PROD', complete: p.publishProd },
      { id: 'validationProd', label: '6) Validation PROD', complete: p.validationProd },
    ];

    const indexActif = sousEtapes.findIndex((x) => x.id === runbookSousEtape);

    const sousEtapeItem = (item, index) => {
      const precedentOk = index === 0 ? true : sousEtapes[index - 1].complete;
      const actif = runbookSousEtape === item.id;
      return (
        <Button
          key={item.id}
          variant={actif ? 'contained' : 'outlined'}
          color={item.complete ? 'primary' : (afficherErreursRunbook ? 'secondary' : 'default')}
          disabled={!precedentOk && !actif}
          onClick={() => setRunbookSousEtape(item.id)}
          style={{ justifyContent: 'flex-start', textTransform: 'none', marginBottom: 8 }}
          fullWidth
        >
          {(item.complete ? '[OK] ' : '[..] ') + item.label}
        </Button>
      );
    };

    const panneauMigration = (cible) => {
      const estProd = cible === 'prod';
      return (
        <Paper style={{ padding: 14, textAlign: 'left' }}>
          <Typography variant="h6">{estProd ? 'Migration PROD' : 'Migration DEV'}</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
            Executer dans PowerShell/terminal VS depuis <span style={{ fontFamily: 'monospace' }}>{DOSSIER_BACKEND_SYNCSTATSLIVE}</span>.
          </Typography>
          {rendreCaseAvecInfo(
            'ASPNETCORE_ENVIRONMENT positionne',
            estProd ? r.migrationProdEnvSet : r.migrationDevEnvSet,
            (value) => changerRunbook(estProd ? 'migrationProdEnvSet' : 'migrationDevEnvSet', value),
            estProd ? 'Utilisez la valeur Production.' : 'Utilisez la valeur Staging.',
            enErreur(estProd ? 'migrationProdEnvSet' : 'migrationDevEnvSet')
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
              {estProd ? COMMANDE_MIGRATION_PROD_ENV : COMMANDE_MIGRATION_DEV_ENV}
            </Typography>
            <Button size="small" variant="outlined" color="primary" onClick={() => copierTexte(estProd ? COMMANDE_MIGRATION_PROD_ENV : COMMANDE_MIGRATION_DEV_ENV, estProd ? 'env PROD' : 'env DEV')}>
              Copier
            </Button>
          </div>
          {rendreCase('`dotnet ef migrations list` execute', estProd ? r.migrationProdMigrationsList : r.migrationDevMigrationsList, (value) => changerRunbook(estProd ? 'migrationProdMigrationsList' : 'migrationDevMigrationsList', value), enErreur(estProd ? 'migrationProdMigrationsList' : 'migrationDevMigrationsList'))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <Typography variant="body2" style={{ fontFamily: 'monospace' }}>{COMMANDE_MIGRATIONS_LIST}</Typography>
            <Button size="small" variant="outlined" color="primary" onClick={() => copierTexte(COMMANDE_MIGRATIONS_LIST, 'migrations list')}>Copier</Button>
          </div>
          {rendreCase('`dotnet ef dbcontext info` execute', estProd ? r.migrationProdDbContextInfo : r.migrationDevDbContextInfo, (value) => changerRunbook(estProd ? 'migrationProdDbContextInfo' : 'migrationDevDbContextInfo', value), enErreur(estProd ? 'migrationProdDbContextInfo' : 'migrationDevDbContextInfo'))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <Typography variant="body2" style={{ fontFamily: 'monospace' }}>{COMMANDE_DBCONTEXT_INFO}</Typography>
            <Button size="small" variant="outlined" color="primary" onClick={() => copierTexte(COMMANDE_DBCONTEXT_INFO, 'dbcontext info')}>Copier</Button>
          </div>
          {rendreCase('`dotnet ef database update` execute', estProd ? r.migrationProdDatabaseUpdate : r.migrationDevDatabaseUpdate, (value) => changerRunbook(estProd ? 'migrationProdDatabaseUpdate' : 'migrationDevDatabaseUpdate', value), enErreur(estProd ? 'migrationProdDatabaseUpdate' : 'migrationDevDatabaseUpdate'))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <Typography variant="body2" style={{ fontFamily: 'monospace' }}>{COMMANDE_MIGRATION_UPDATE}</Typography>
            <Button size="small" variant="outlined" color="primary" onClick={() => copierTexte(COMMANDE_MIGRATION_UPDATE, 'database update')}>Copier</Button>
          </div>
          {rendreCase('Derniere migration verifiee dans `__EFMigrationsHistory`', estProd ? r.migrationProdHistoriqueVerifie : r.migrationDevHistoriqueVerifie, (value) => changerRunbook(estProd ? 'migrationProdHistoriqueVerifie' : 'migrationDevHistoriqueVerifie', value), enErreur(estProd ? 'migrationProdHistoriqueVerifie' : 'migrationDevHistoriqueVerifie'))}
        </Paper>
      );
    };

    const panneauPublish = (cible) => {
      const estProd = cible === 'prod';
      return (
        <Paper style={{ padding: 14, textAlign: 'left' }}>
          <Typography variant="h6">{estProd ? 'Publish PROD (Visual Studio)' : 'Publish DEV (Visual Studio)'}</Typography>
          {!estProd && (
            <div>
              {rendreCase('Pre-requis: solution compile en local', r.publishDevPrereqCompileLocal, (value) => changerRunbook('publishDevPrereqCompileLocal', value), enErreur('publishDevPrereqCompileLocal'))}
              {rendreCase('Pre-requis: profils DEV/PROD existants', r.publishDevPrereqProfilsExistants, (value) => changerRunbook('publishDevPrereqProfilsExistants', value), enErreur('publishDevPrereqProfilsExistants'))}
            </div>
          )}
          {estProd && (
            <div>{rendreCase('Verification rapide: migration PROD faite, health DEV OK, aucun correctif en attente', r.publishProdVerificationRapideFaite, (value) => changerRunbook('publishProdVerificationRapideFaite', value), enErreur('publishProdVerificationRapideFaite'))}</div>
          )}
          {!estProd && rendreCase('Ouvrir SyncStatsLive.sln', r.publishDevOuvrirSolution, (value) => changerRunbook('publishDevOuvrirSolution', value), enErreur('publishDevOuvrirSolution'))}
          {!estProd && rendreCaseAvecInfo(
            'Clic droit projet "SyncStatsLive" -> Publish',
            r.publishDevClicDroitPublish,
            (value) => changerRunbook('publishDevClicDroitPublish', value),
            'Utilisez le projet web cible.',
            enErreur('publishDevClicDroitPublish')
          )}
          {estProd && rendreCase('Clic droit projet web -> Publish', r.publishProdClicDroitPublish, (value) => changerRunbook('publishProdClicDroitPublish', value), enErreur('publishProdClicDroitPublish'))}
          {estProd && rendreCase('Selectionner profil PROD', r.publishProdSelectionProfilProd, (value) => changerRunbook('publishProdSelectionProfilProd', value), enErreur('publishProdSelectionProfilProd'))}
          {!estProd && rendreCase('Selectionner profil DEV', r.publishDevSelectionProfilDev, (value) => changerRunbook('publishDevSelectionProfilDev', value), enErreur('publishDevSelectionProfilDev'))}
          {rendreCase(
            estProd
              ? 'Parametres profil PROD verifies: MSDeploy, WMSVC, URL PROD, DeployIisAppPath=syncstats.live, EnvironmentName=Production, AppOffline=true, SkipExtraFiles=true, net5.0'
              : 'Parametres profil DEV verifies: MSDeploy, WMSVC, URL DEV, DeployIisAppPath=dev.syncstats.live, EnvironmentName=Staging, AppOffline=true, SkipExtraFiles=true, net5.0',
            estProd ? r.publishProdParametresVerifies : r.publishDevParametresVerifies,
            (value) => changerRunbook(estProd ? 'publishProdParametresVerifies' : 'publishDevParametresVerifies', value),
            enErreur(estProd ? 'publishProdParametresVerifies' : 'publishDevParametresVerifies')
          )}
          {rendreCase('Validate Connection executee', estProd ? r.publishProdValidateConnection : r.publishDevValidateConnection, (value) => changerRunbook(estProd ? 'publishProdValidateConnection' : 'publishDevValidateConnection', value), enErreur(estProd ? 'publishProdValidateConnection' : 'publishDevValidateConnection'))}
          {rendreCase(
            estProd ? 'Database publish confirme desactive' : 'Settings verifies: Release, net5.0, Database publish desactive',
            estProd ? r.publishProdDatabasePublishDesactive : r.publishDevSettingsVerifies,
            (value) => changerRunbook(estProd ? 'publishProdDatabasePublishDesactive' : 'publishDevSettingsVerifies', value),
            enErreur(estProd ? 'publishProdDatabasePublishDesactive' : 'publishDevSettingsVerifies')
          )}
          {rendreCase('Cliquer Publish', estProd ? r.publishProdCliquerPublish : r.publishDevCliquerPublish, (value) => changerRunbook(estProd ? 'publishProdCliquerPublish' : 'publishDevCliquerPublish', value), enErreur(estProd ? 'publishProdCliquerPublish' : 'publishDevCliquerPublish'))}
        </Paper>
      );
    };

    const panneauValidation = (cible) => {
      const estProd = cible === 'prod';
      return (
        <Paper style={{ padding: 14, textAlign: 'left' }}>
          <Typography variant="h6">{estProd ? 'Validation PROD (10-15 min)' : 'Validation DEV (5-10 min)'}</Typography>
          {rendreCase(estProd ? 'Site accessible: https://syncstats.live' : 'Site charge: https://dev.syncstats.live', estProd ? r.validationProdSiteAccessible : r.validationDevSiteCharge, (value) => changerRunbook(estProd ? 'validationProdSiteAccessible' : 'validationDevSiteCharge', value), enErreur(estProd ? 'validationProdSiteAccessible' : 'validationDevSiteCharge'))}
          {rendreCase('Login OK', estProd ? r.validationProdLoginOk : r.validationDevLoginOk, (value) => changerRunbook(estProd ? 'validationProdLoginOk' : 'validationDevLoginOk', value), enErreur(estProd ? 'validationProdLoginOk' : 'validationDevLoginOk'))}
          {rendreCase(estProd ? 'Flux principal OK (calendrier, event/presence)' : 'Calendrier/sommaire match OK', estProd ? r.validationProdFluxPrincipalOk : r.validationDevCalendrierOk, (value) => changerRunbook(estProd ? 'validationProdFluxPrincipalOk' : 'validationDevCalendrierOk', value), enErreur(estProd ? 'validationProdFluxPrincipalOk' : 'validationDevCalendrierOk'))}
          {!estProd && rendreCase('Ajout evenement/presence OK', r.validationDevAjoutPresenceOk, (value) => changerRunbook('validationDevAjoutPresenceOk', value), enErreur('validationDevAjoutPresenceOk'))}
          {rendreCase('Logs sans erreurs critiques', estProd ? r.validationProdLogsSansErreurCritique : r.validationDevLogsSansErreurCritique, (value) => changerRunbook(estProd ? 'validationProdLogsSansErreurCritique' : 'validationDevLogsSansErreurCritique', value), enErreur(estProd ? 'validationProdLogsSansErreurCritique' : 'validationDevLogsSansErreurCritique'))}
          {!estProd && rendreCase('Pas d erreur SQL schema', r.validationDevPasErreurSchemaSql, (value) => changerRunbook('validationDevPasErreurSchemaSql', value), enErreur('validationDevPasErreurSchemaSql'))}
          {estProd && rendreCase('Pas de migration auto au demarrage', r.validationProdPasMigrationAuto, (value) => changerRunbook('validationProdPasMigrationAuto', value), enErreur('validationProdPasMigrationAuto'))}
          {rendreCase('Si synchro active: ACK/outbox OK', estProd ? r.validationProdAckOutboxOk : r.validationDevAckOutboxOk, (value) => changerRunbook(estProd ? 'validationProdAckOutboxOk' : 'validationDevAckOutboxOk', value), enErreur(estProd ? 'validationProdAckOutboxOk' : 'validationDevAckOutboxOk'))}
          {!estProd && rendreCase('Si DEV KO: process stoppe, pas de passage PROD', r.validationDevStopSiKo, (value) => changerRunbook('validationDevStopSiKo', value), enErreur('validationDevStopSiKo'))}
        </Paper>
      );
    };

    let panneauDroite = panneauMigration('dev');
    if (runbookSousEtape === 'publishDev') panneauDroite = panneauPublish('dev');
    if (runbookSousEtape === 'validationDev') panneauDroite = panneauValidation('dev');
    if (runbookSousEtape === 'migrationProd') panneauDroite = panneauMigration('prod');
    if (runbookSousEtape === 'publishProd') panneauDroite = panneauPublish('prod');
    if (runbookSousEtape === 'validationProd') panneauDroite = panneauValidation('prod');

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper style={{ padding: 12, background: '#f8fbff', textAlign: 'left' }}>
            <Typography variant="subtitle2">REGLE D OR</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
              Toujours: Migration DEV -&gt; Publish DEV -&gt; Validation DEV -&gt; Migration PROD -&gt; Publish PROD -&gt; Validation PROD
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
              Focus actuel: {sousEtapes[indexActif] ? sousEtapes[indexActif].label : 'Sous-etape'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper style={{ padding: 12, textAlign: 'left' }}>
            <Typography variant="subtitle2" style={{ marginBottom: 10 }}>Sous-etapes</Typography>
            {sousEtapes.map((item, index) => sousEtapeItem(item, index))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          {panneauDroite}
          <Paper style={{ padding: 12, marginTop: 12, background: '#fffaf2', textAlign: 'left' }}>
            <Typography variant="subtitle2">Incidents courants</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              ERROR_USER_UNAUTHORIZED: credentials Web Deploy ou site cible incorrect.
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              ERROR_INSUFFICIENT_ACCESS_TO_SITE_FOLDER: conserver SkipExtraFilesOnServer=true puis republier.
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              Mauvaise base: verifier ASPNETCORE_ENVIRONMENT et web.config.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const rendrePost = () => (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        {rendreCaseAvecInfo(
          'Navigation de base validee',
          etat.validationsPostDeploiement.navigationBaseOk,
          (value) => changerPost('navigationBaseOk', value),
          'Verifier la navigation principale sans erreur: pages d accueil, menus, parcours standards et chargement ecrans critiques.'
        )}
      </Grid>
      <Grid item xs={12}>
        {rendreCaseAvecInfo(
          'Connexion utilisateur validee',
          etat.validationsPostDeploiement.connexionUtilisateurOk,
          (value) => changerPost('connexionUtilisateurOk', value),
          'Tester au moins un login valide et un cas invalide; confirmer session, droits et deconnexion corrects.'
        )}
      </Grid>
      <Grid item xs={12}>
        {rendreCaseAvecInfo(
          'Parcours calendrier / presence valide',
          etat.validationsPostDeploiement.parcoursPresenceOk,
          (value) => changerPost('parcoursPresenceOk', value),
          'Verifier lecture et ajout de presence/evenement sur le calendrier, puis controle visuel des donnees attendues.'
        )}
      </Grid>
      <Grid item xs={12}>
        {rendreCaseAvecInfo(
          'ACK synchronisation valide',
          etat.validationsPostDeploiement.ackSyncOk,
          (value) => changerPost('ackSyncOk', value),
          'Si la synchro est active, verifier les ACK sur `/api/sync/ack` et confirmer un flux normal sans pic d erreurs.'
        )}
      </Grid>
      <Grid item xs={12}>
        {rendreCaseAvecInfo(
          'Logs sans erreur critique',
          etat.validationsPostDeploiement.logsSansErreurCritique,
          (value) => changerPost('logsSansErreurCritique', value),
          'Surveiller logs applicatifs/IIS: aucune erreur SQL critique, aucune boucle restart, aucune exception bloquante.'
        )}
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          label="Notes post-deploiement"
          value={etat.validationsPostDeploiement.notes}
          onChange={(event) => changerPost('notes', event.target.value)}
        />
      </Grid>
    </Grid>
  );

  const rendreDecision = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="decision-label">Decision finale</InputLabel>
          <Select
            labelId="decision-label"
            value={etat.decisionFinale}
            onChange={(event) => changerChamp('decisionFinale', event.target.value)}
            label="Decision finale"
          >
            <MenuItem value="go">GO</MenuItem>
            <MenuItem value="go_avec_surveillance">GO avec surveillance renforcee</MenuItem>
            <MenuItem value="no_go">NO GO</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          variant="outlined"
          label="Commentaire de decision"
          value={etat.commentaireDecision}
          onChange={(event) => changerChamp('commentaireDecision', event.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          label="Incidents ou points d attention"
          value={etat.incidents}
          onChange={(event) => changerChamp('incidents', event.target.value)}
        />
      </Grid>
    </Grid>
  );

  const rendreEtape = () => {
    if (etapeActive === 0) return rendreContexte();
    if (etapeActive === 1) return rendrePlesk();
    if (etapeActive === 2) return rendreDb();
    if (etapeActive === 3) return rendreRunbook();
    if (etapeActive === 4) return rendrePost();
    if (etapeActive === 5) return rendreDecision();
    return <DeploiementResume rapport={rapport} />;
  };

  const annulerDeploiementEnCours = () => {
    const confirmation = window.confirm('Voulez-vous annuler ce deploiement en cours et supprimer le brouillon local ?');
    if (!confirmation) {
      return;
    }
    setEtat(etatInitial);
    setEtapeActive(0);
    setErreur('');
    setSucces('Le deploiement en cours a ete annule. Le brouillon local a ete supprime.');
    setDerniereSauvegarde('');
    localStorage.removeItem(CLE_BROUILLON_DEPLOIEMENT);
  };

  return (
    <Paper style={{ padding: 16 }}>
      <Typography variant="h5">Deploiement plateformeweb</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
        Formulaire guide avec controles critiques avant la validation finale.
      </Typography>
      <Typography variant="caption" color="textSecondary" style={{ marginTop: 6, display: 'block' }}>
        {derniereSauvegarde ? ('Brouillon sauvegarde automatiquement: ' + new Date(derniereSauvegarde).toLocaleString()) : 'Brouillon local non initialise.'}
      </Typography>

      <Stepper activeStep={etapeActive} alternativeLabel style={{ marginTop: 16, marginBottom: 16 }}>
        {etapes.map((etape) => (
          <Step key={etape}>
            <StepLabel>{etape}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {rendreEtape()}

      <Grid container spacing={2} style={{ marginTop: 12 }}>
        <Grid item>
          <Button variant="outlined" disabled={etapeActive === 0 || soumissionEnCours} onClick={allerPrecedent}>
            Precedent
          </Button>
        </Grid>
        <Grid item>
          {etapeActive < etapes.length - 1 ? (
            <Button variant="contained" color="primary" onClick={allerSuivant} disabled={soumissionEnCours}>
              Suivant
            </Button>
          ) : (
            <Button variant="contained" color="primary" onClick={soumettre} disabled={soumissionEnCours}>
              {soumissionEnCours ? 'Enregistrement...' : 'Soumettre le rapport'}
            </Button>
          )}
        </Grid>
        <Grid item>
          <Button variant="text" color="secondary" onClick={annulerDeploiementEnCours} disabled={soumissionEnCours}>
            Annuler le deploiement en cours
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={Boolean(erreur)}
        autoHideDuration={5000}
        onClose={() => setErreur('')}
        message={erreur}
      />
      <Snackbar
        open={Boolean(succes)}
        autoHideDuration={4000}
        onClose={() => setSucces('')}
        message={succes}
      />
    </Paper>
  );
}

export default DeploiementForm;



// WEBPACK FOOTER //
// src/components/deploiements/DeploiementForm.js